// Archive status for a closed-out permit (WF-019/020, CORE-073, DEC-GOV-007): the job,
// its attempts and errors, the unresolved retention decision, and the stored files with
// their checksums. Archiving runs separately and can never reopen work.
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { RuleRefs } from '@/components/evidence'
import { Badge, Banner, Button, Card, EmptyState, KeyValue, SectionTitle } from '@/components/ui'
import type { PermitBundle } from '@/domain/types'
import { fmtDateTime, fmtTime } from '@/lib/format'
import { createArchiveDownloadUrl, describeFailure, runArchiveWorker } from './archive'
import { ARCHIVE_BUCKET, archiveStatusText, artifactKindLabel, canProcessArchiveNow, fmtBytes, retentionText, SIGNED_URL_SECONDS } from './logic'

type Artifact = NonNullable<PermitBundle['archive']>['artifacts'][number]

function Digest({ value }: { value: string }) {
  return <span className="code block break-all text-xs text-ink">{value || '-'}</span>
}

function ArtifactItem({ a }: { a: Artifact }) {
  const [link, setLink] = useState<{ url: string; expiresAt: number } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const downloadable = !a.storage_bucket || a.storage_bucket === ARCHIVE_BUCKET

  // A signed link stops working after five minutes; drop it then so a dead link is never shown.
  useEffect(() => {
    if (!link) return
    const t = window.setTimeout(() => setLink(null), Math.max(0, link.expiresAt - Date.now()))
    return () => window.clearTimeout(t)
  }, [link])

  const makeLink = async () => {
    setBusy(true)
    setError(null)
    try {
      const url = await createArchiveDownloadUrl(a.storage_path)
      setLink({ url, expiresAt: Date.now() + SIGNED_URL_SECONDS * 1000 })
    } catch (e) {
      setError(await describeFailure(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <li className="rounded-lg border border-line bg-white p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="font-semibold text-ink">
          {artifactKindLabel(a.artifact_kind)} <span className="code text-xs font-normal text-ink-faint">{a.artifact_kind}</span>
        </p>
        <span className="text-sm text-ink-soft">{fmtBytes(a.byte_count)}</span>
      </div>
      <div className="mt-2">
        <KeyValue
          cols={1}
          items={[
            ['SHA-256 checksum', <Digest value={a.sha256} />],
            ['Stored at', <span className="code break-all text-xs">{`${a.storage_bucket}/${a.storage_path}`}</span>],
            ['Render template', a.render_template_version],
          ]}
        />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {!downloadable ? (
          <p className="text-sm text-ink-soft">Download links are made only for the {ARCHIVE_BUCKET} store.</p>
        ) : link ? (
          <>
            <a
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="tap inline-flex items-center rounded-lg bg-brand px-4 font-semibold text-white hover:bg-brand-dark"
            >
              Open {artifactKindLabel(a.artifact_kind)}
            </a>
            <span className="text-xs text-ink-soft">Link works until {fmtTime(new Date(link.expiresAt).toISOString())} GST (5 minutes).</span>
          </>
        ) : (
          <Button variant="secondary" onClick={() => void makeLink()} loading={busy}>
            Get download link
          </Button>
        )}
      </div>
      {error ? (
        <Banner tone="bad" title="No download link" className="mt-2">
          {error}
        </Banner>
      ) : null}
    </li>
  )
}

export function ArchiveCard({ bundle }: { bundle: PermitBundle }) {
  const qc = useQueryClient()
  const [run, setRun] = useState<{ state: 'idle' | 'running' | 'done' | 'failed'; message?: string }>({ state: 'idle' })
  const archive = bundle.archive
  const afterCloseout = bundle.permit.lifecycle_state === 'closed_out' || bundle.permit.lifecycle_state === 'archived'

  const processNow = async () => {
    setRun({ state: 'running' })
    try {
      await runArchiveWorker()
      setRun({ state: 'done' })
    } catch (e) {
      setRun({ state: 'failed', message: await describeFailure(e) })
    } finally {
      // Refetch either way: an attempt may have been recorded even when the call failed.
      await qc.invalidateQueries()
    }
  }

  const title = (
    <SectionTitle aside={<RuleRefs ruleIds={['CORE-073']} decisionIds={['DEC-GOV-007']} />}>Archive</SectionTitle>
  )
  const explanation = (
    <p className="text-sm text-ink-soft">
      Archiving runs separately after the Responsible Supervisor closes out. It stores a fixed copy of the closed record (the record
      bundle and the printable permit) with checksums. It cannot reopen the permit or restart work, and a failed attempt only
      means it is tried again.
    </p>
  )

  if (!archive) {
    return (
      <Card>
        {title}
        {afterCloseout ? (
          <Banner tone="warn" title="No archive job is visible for this permit">
            The permit is closed out, but the server did not return an archive job. The permit stays closed out; this needs checking.
          </Banner>
        ) : (
          <EmptyState title="Not archived yet">Archiving starts when the Responsible Supervisor closes out the permit.</EmptyState>
        )}
        <div className="mt-3">{explanation}</div>
      </Card>
    )
  }

  const { job, artifacts } = archive
  const status = archiveStatusText(job.status)
  const retention = retentionText(job.retention_status)

  return (
    <Card>
      {title}
      <div className="space-y-4">
        {explanation}

        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={status.tone}>{status.label}</Badge>
          <span className="code text-xs text-ink-faint">{job.status}</span>
          <span className="text-sm text-ink-soft">{status.detail}</span>
        </div>

        <KeyValue
          items={[
            ['Attempts', String(job.attempt_count)],
            ['Last error', job.last_error ? <span className="whitespace-pre-wrap text-bad">{job.last_error}</span> : 'None'],
            ['Requested', fmtDateTime(job.requested_at)],
            ['Completed', job.completed_at ? fmtDateTime(job.completed_at) : 'Not yet'],
            ['Retention', <Badge tone={retention.tone}>{retention.label}</Badge>],
            ['Record digest at closeout (SHA-256)', <Digest value={job.bundle_digest} />],
          ]}
        />

        {canProcessArchiveNow(job.status) ? (
          <div className="rounded-lg border border-line bg-paper p-3">
            <p className="text-sm text-ink">
              The archive worker runs separately from the permit. This asks it to process the waiting archive jobs now. It does not
              change the permit or anyone&apos;s signatures.
            </p>
            <div className="mt-2">
              <Button variant="secondary" onClick={() => void processNow()} loading={run.state === 'running'}>
                Process archive now
              </Button>
            </div>
          </div>
        ) : null}

        {run.state === 'done' ? (
          <Banner tone="info" title="Archive worker asked to run">
            The status above is refreshed. If it still says queued, the worker may need another moment.
          </Banner>
        ) : null}
        {run.state === 'failed' ? (
          <Banner tone="bad" title="The archive worker did not run">
            <p>{run.message}</p>
            <p className="mt-1">The permit is unchanged and the job stays in the queue.</p>
          </Banner>
        ) : null}

        <div>
          <p className="mb-2 text-sm font-semibold text-ink">Archived files</p>
          {artifacts.length === 0 ? (
            <EmptyState title="No files yet">Files appear here when the archive copy has been made.</EmptyState>
          ) : (
            <ul className="space-y-2">
              {artifacts.map((a) => (
                <ArtifactItem key={`${a.artifact_kind}-${a.storage_path}`} a={a} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </Card>
  )
}
